import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import _ from 'lodash';

import styles from './Matching.module.scss';
import DroppableArea from './DroppableArea';

export type MatchingItem = {
  resourceId: string;
  draggable: string;
  droppable: string;
};

type MatchingProps = {
  matchingItems: MatchingItem[];
  matchingResponse?: object;
  showFeedback?: boolean;
  disabled?: boolean;
  sameHeight?: boolean;
  width?: number;
  onChange?: ({ completed, response, score }: onChangePayload) => void;
  styleType?: 'default' | 'ordering';
};

export type Item = {
  draggableId?: string;
  draggableText?: string;
  droppableId: string;
  droppableText?: string;
  type: 'source' | 'destination';
  order?: number;
};

export type onChangePayload = {
  response: object;
  score: number;
  completed: boolean;
};

function Matching({
  matchingItems,
  matchingResponse,
  showFeedback,
  disabled,
  sameHeight,
  width,
  onChange,
  styleType = 'default',
}: MatchingProps) {
  const [items, setItems] = useState<Item[]>();
  const [response, setResponse] = useState<any>();
  const [changeDataPayload, setChangeDataPayload] = useState<onChangePayload>();
  const [minHeight, setMinHeight] = useState<number>();
  const [touched, setTouched] = useState<boolean>(false);

  useEffect(() => {
    const initialItems: Item[] = [];

    const sanitizedItems = sanitizeMatchingItems(matchingItems);

    const order: number[] = [];
    for (let i = 0; i < _.size(sanitizedItems); i++) order.push(i);
    const shuffle = _.shuffle(order);

    _.forEach(
      sanitizedItems,
      ({ resourceId, draggable, droppable }, index: number) => {
        initialItems.push({
          draggableId: `draggable-${resourceId}`,
          draggableText: draggable,
          droppableId: `droppable-source-${resourceId}`,
          droppableText: undefined,
          type: 'source',
          order: shuffle[index],
        });
        initialItems.push({
          draggableId: undefined,
          draggableText: undefined,
          droppableId: `droppable-destination-${resourceId}`,
          droppableText: droppable,
          type: 'destination',
        });
      }
    );
    setItems(initialItems);
  }, [matchingItems]);

  useEffect(() => {
    if (!_.isEmpty(matchingResponse) && _.isEmpty(response))
      setResponse(matchingResponse);

    // check completed and calculate score
    if (!_.isEmpty(response)) {
      let completed = true;
      const maxResponses = sanitizeMatchingItems(matchingItems).length;
      let correctResponses = 0;
      _.forEach(_.keys(response), (key: string) => {
        if (_.includes(key, 'destination')) {
          if (response[key] === undefined) {
            completed = false;
          } else {
            if (checkResponse(key, response[key])) correctResponses++;
          }
        }
      });

      const updatedChangeDataPayload = {
        completed,
        response,
        score: correctResponses / maxResponses,
      };

      setChangeDataPayload((prevState) => {
        // if object is the same, do not set state (to avoid trigger useEffect on changeDataPayload)
        if (_.isEqual(prevState, updatedChangeDataPayload)) return;
        return updatedChangeDataPayload;
      });
    }
  }, [matchingResponse, response, matchingItems]); // eslint-disable-line

  useEffect(() => {
    onChange && changeDataPayload && onChange(changeDataPayload);
  }, [changeDataPayload]); // eslint-disable-line

  function sanitizeMatchingItems(items: MatchingItem[]) {
    // remove empty values
    return items.filter((m) => m.draggable && m.droppable);
  }

  function checkResponse(droppableId: string, draggableId: string) {
    // first, will match ids:
    const onlyIdFromDroppableId = _.replace(
      droppableId,
      'droppable-destination-',
      ''
    );
    const onlyIdFromDraggableId = _.replace(draggableId, 'draggable-', '');
    const matchIds = onlyIdFromDroppableId === onlyIdFromDraggableId;

    // then, will try to match with expected text
    // for instance: two droppables as "hero" (same text)
    const textThatMatchesWithThisDraggagle = _.find(
      items,
      (i) => i.droppableId === `droppable-destination-${onlyIdFromDraggableId}`
    )?.droppableText;
    const droppableText = _.find(
      items,
      (i) => i.droppableId === droppableId
    )?.droppableText;
    const matchTexts = textThatMatchesWithThisDraggagle === droppableText;

    // it will accept as correct one or other
    return matchIds || matchTexts;
  }

  function handleOnDragEnd(result: DropResult, provided: ResponderProvided) {
    // console.log(
    //   `${result.draggableId} from ${result.source.droppableId} to ${result.destination?.droppableId}`
    // );

    if (!result.destination?.droppableId) {
      console.warn('destination is undefined');
      return;
    }
    if (result.source.droppableId === result.destination?.droppableId) {
      console.warn('source and destination are the same');
      return;
    }

    let updatedResponse = _.cloneDeep(response);

    // init response (if undefined)
    if (!updatedResponse) {
      _.forEach(items, (item) => {
        updatedResponse = {
          ...updatedResponse,
          [item.droppableId]: item.draggableId,
        };
      });
    }

    // swap
    const prevUpdatedResponse = _.cloneDeep(updatedResponse);
    updatedResponse[result.destination.droppableId] = result.draggableId;
    updatedResponse[result.source.droppableId] =
      prevUpdatedResponse[result.destination.droppableId];

    setResponse(updatedResponse);
  }

  function renderDroppables(type: 'source' | 'destination') {
    const ordered = _.orderBy(items, 'order');

    const merged = _.map(ordered, (item) => {
      if (!response) return item;
      const responseDraggableId = response[item.droppableId];
      return {
        ...item,
        draggableId: responseDraggableId,
        draggableText: responseDraggableId
          ? _.find(items, (item) => item.draggableId === responseDraggableId)
              ?.draggableText
          : undefined,
      };
    });

    const filtered = _.filter(merged, (item) => item.type === type);

    return _.map(filtered, (item) => {
      const isResponseCorrect = checkResponse(
        item.droppableId,
        item.draggableId
      );
      return (
        <DroppableArea
          key={item.droppableId}
          correct={showFeedback && isResponseCorrect}
          incorrect={showFeedback && !isResponseCorrect}
          disabled={disabled}
          onMount={(data) => {
            if (!sameHeight) return;
            if (touched) return;
            setMinHeight((prevMinHeight = 0) =>
              data.height > prevMinHeight ? data.height : prevMinHeight
            );
          }}
          minHeight={minHeight}
          width={width}
          styleType={styleType}
          {...item}
        />
      );
    });
  }

  return (
    <DragDropContext
      onDragEnd={handleOnDragEnd}
      onDragStart={() => setTouched(true)}
    >
      <main className={styles.root}>
        <section className={styles.left}>{renderDroppables('source')}</section>
        <section className={styles.right}>
          {renderDroppables('destination')}
        </section>
      </main>
    </DragDropContext>
  );
}

export default Matching;
